// src/components/PreExperiment.js
import React, { useEffect, useState } from 'react';
import { Container, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useToken from '../../hooks/useToken';
import ProgressTracker from '../shared/ProgressTracker';
import useProgressRedirect from '../../hooks/useProgressRedirect';


function PreExperiment() {
  const { token, refreshAccessToken } = useToken();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useProgressRedirect("PRE_EXPERIMENT"); // only redirect if progress does not match this page

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await fetch("/api"+'/questionnaire/pre', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setQuestions(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load questionnaire.');
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [token]);

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleRankingChange = (questionId, item, value) => {
    const prev = answers[questionId] || {};
    setAnswers(prevAnswers => ({
      ...prevAnswers,
      [questionId]: { ...prev, [item]: value }
    }));
  };

  const handleCheckboxChange = (questionId, choice) => {
    const prev = answers[questionId] || [];
    const updated = prev.includes(choice)
      ? prev.filter(item => item !== choice)
      : [...prev, choice];
    setAnswers(prev => ({ ...prev, [questionId]: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); // start loading
    
    // Validation: Ensure all questions are answered
    const unansweredQuestions = questions.filter((q) => {
      const answer = answers[q.id];
      if (q.question_type === 'TEXT' || q.question_type === 'NUMERIC') {
        return !answer || answer.trim() === '';
      } else if (q.question_type === 'MCQ') {
        return !answer;
      } else if (q.question_type === 'CHECKBOX') {
        return !answer || answer.length === 0;
      } else if (q.question_type === 'RANKING') {
        // Check all items are ranked
        if (!answer || typeof answer !== 'object') return true;
        const choices = q.choices || [];
        return choices.some(choice => {
          const item = typeof choice === 'string' ? choice : choice.text;
          return !answer[item] || answer[item].trim() === '';
        });
      }
      return false;
    });

    if (unansweredQuestions.length > 0) {
      setError('Please answer all the questions before submitting.');
      setSubmitting(false);
      return;
    }

    // Validation: Check for duplicate ranks in RANKING questions
    const duplicateRankQuestions = questions.filter((q) => {
      if (q.question_type === 'RANKING') {
        const answer = answers[q.id];
        if (!answer || typeof answer !== 'object') return false;
        
        const ranks = Object.values(answer).map(v => v.trim()).filter(v => v !== '');
        const uniqueRanks = new Set(ranks);
        return ranks.length !== uniqueRanks.size; // true if duplicates exist
      }
      return false;
    });

    if (duplicateRankQuestions.length > 0) {
      setError('Please ensure each ranking question has unique ranks (no duplicates).');
      setSubmitting(false);
      return;
    }

    // Popup confirmation
    const confirmSubmit = window.confirm(
      "Are you sure you want to submit your answers? You will not be able to change them after submission."
    );

    if (!confirmSubmit) {
      setSubmitting(false);
      return; // User canceled the submission
    }
    
    const formattedAnswers = Object.entries(answers).map(([questionId, answer]) => ({
      questionnaire_id: parseInt(questionId),
      answer: Array.isArray(answer) 
        ? answer.join(', ') 
        : typeof answer === 'object' 
          ? JSON.stringify(answer)
          : answer,
    }));
  
    try {
      const authToken = token || await refreshAccessToken();
  
      const res = await fetch("/api"+'/questionnaire/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ answers: formattedAnswers, type: 'PRE' }),
      });
  
      if (res.ok) {
        const progressRes = await fetch("/api"+'/player/progress', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ progress_step: 'DOCUMENTATION_INTRO' }),
        });
  
        if (progressRes.ok) {
          navigate('/documentation-intro', { replace: true });
          return;
        } else {
          const err = await progressRes.json();
          setError(err.message || 'Failed to update progress.');
        }
      } else {
        const err = await res.json();
        setError(err.message || 'Failed to submit answers.');
      }
    } catch (err) {
      setError('Error submitting answers.');
    }
    setSubmitting(false);
  };
  
  

  return (
    <Container className="py-4">
      <ProgressTracker currentStep="pre_experiment" />
      {(loading || submitting) && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : (<>

      <h2 className="mb-4">📝 Pre-Experiment Questionnaire</h2>
      <Form onSubmit={handleSubmit}>
        {questions.map((q, index) => (
          <Form.Group key={q.id} className="mb-4">
            <Form.Label><strong>{index + 1}. {q.question_text}</strong></Form.Label>

            {q.question_type === 'TEXT' && (
              <Form.Control
                type="text"
                value={answers[q.id] || ''}
                onChange={(e) => handleInputChange(q.id, e.target.value)}
              />
            )}

            {q.question_type === 'NUMERIC' && (
              <Form.Control
                type="number"
                step="any"
                value={answers[q.id] || ''}
                onChange={(e) => handleInputChange(q.id, e.target.value)}
                placeholder="Enter a number"
              />
            )}

            {q.question_type === 'RANKING' && Array.isArray(q.choices) && (
              <div className="ms-3">
                {q.choices.map((choice, idx) => {
                  const label = typeof choice === 'string' ? choice : choice.text;
                  return (
                    <Form.Group key={idx} className="mb-2 d-flex align-items-center">
                      <Form.Label className="mb-0 me-3" style={{ minWidth: '250px' }}>
                        {label}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="6"
                        step="1"
                        value={answers[q.id]?.[label] || ''}
                        onChange={(e) => handleRankingChange(q.id, label, e.target.value)}
                        placeholder="1-6"
                        style={{ width: '80px' }}
                      />
                    </Form.Group>
                  );
                })}
              </div>
            )}

            {q.question_type === 'MCQ' && Array.isArray(q.choices) && q.choices.map((choice, idx) => {
              const label = typeof choice === 'string' ? choice : choice.text;
              return (
                <Form.Check
                  key={idx}
                  type="radio"
                  name={`mcq-${q.id}`}
                  label={label}
                  value={label}
                  onChange={() => handleInputChange(q.id, label)}
                  checked={answers[q.id] === label}
                />
              );
            })}

            {q.question_type === 'CHECKBOX' && Array.isArray(q.choices) && q.choices.map((choice, idx) => {
              const label = typeof choice === 'string' ? choice : choice.text;
              return (
                <Form.Check
                  key={idx}
                  type="checkbox"
                  label={label}
                  checked={answers[q.id]?.includes(label) || false}
                  onChange={() => handleCheckboxChange(q.id, label)}
                />
              );
            })}
          </Form.Group>
        ))}
        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
        <div className="d-flex gap-2">
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" /> Submitting...
              </>
            ) : (
              'Submit'
            )}
          </Button>
          <Button 
            variant="warning" 
            onClick={async () => {
              const authToken = token || await refreshAccessToken();
              const progressRes = await fetch("/api"+'/player/progress', {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({ progress_step: 'DOCUMENTATION_INTRO' }),
              });
              if (progressRes.ok) {
                navigate('/documentation-intro', { replace: true });
              }
            }}
          >
            ⚠️ Skip (Testing)
          </Button>
        </div>
      </Form>
      </>)}
    </Container>
  );
}

export default PreExperiment;
